/**
 * The permission map to gate the UI on.
 *
 * There are two per-feature maps on the user object and they do not agree:
 *
 *  - `role_settings` is read live from `user_roles.role_settings` on every
 *    `/userinfo` call. It is what the server itself enforces — the same map
 *    behind `User::hasPermission()`, the `permission:` middleware and
 *    `Helper::seesAllCompanyJobs()`.
 *  - `roles` is a snapshot of `users.roles`, copied at account creation and
 *    never refreshed. It is NULL or empty for most users.
 *
 * Every gate in the app read `roles`. For a Supervisor that meant the menu was
 * empty, the router bounced /projects to the dashboard and every V/A/E/D button
 * was hidden — while the API happily served the company's projects, because the
 * server was reading the other map. The only thing that appeared to fix it was
 * moving the user to Admin or Full Admin, which works by skipping the map
 * entirely via the isFullAdmin() bypass rather than by being correct.
 *
 * Preferring `role_settings` is the whole fix. A Supervisor holding
 * `jobs__View` now sees Projects — and sees ALL of the company's projects,
 * because that is already what the server returns for them; the client was the
 * only thing in the way.
 *
 * The `roles` fallback is for sessions that signed in before the server began
 * sending `role_settings`; their cached user object has no such field and
 * without this they would be demoted until they signed in again. It triggers
 * only when `role_settings` is ABSENT, never when it is present and empty — a
 * role that genuinely grants nothing is an answer, not a missing value, and
 * must not quietly inherit a stale snapshot.
 */
export function permissionMap(user: any): Record<string, boolean> {
    if (!user) return {};

    if (user.role_settings && typeof user.role_settings === 'object') {
        return user.role_settings as Record<string, boolean>;
    }

    // Legacy fallback — see above. Sessions predating the server-side field.
    if (user.roles && typeof user.roles === 'object') {
        return user.roles as Record<string, boolean>;
    }

    return {};
}

/**
 * Does this user hold `<slug>__<action>`?
 *
 * Fails CLOSED: a key the role does not mention is denied. Admin bypasses are
 * deliberately NOT applied here — callers that allow them already call
 * isFullAdmin() first, and folding it in would hide which gates bypass and
 * which do not.
 */
export function hasPermission(user: any, slug: string, action = 'View'): boolean {
    if (!slug) return false;

    // Route names may carry a query template ("summary_jobs?summary=:id");
    // the stored slug is the part before the "?".
    const key = String(slug).split('?')[0] + '__' + action;

    return permissionMap(user)[key] === true;
}

/**
 * Does this user hold a full permission KEY, e.g. "timesheetoverview__View"?
 *
 * The same check as hasPermission(), for the many call sites that already hold
 * the joined key rather than a slug and an action. It exists to replace the
 * hand-rolled `user.roles?.[key]` lookups that were copied into ~18 files in
 * the Teams/timesheet family: those read the STALE `users.roles` snapshot
 * directly, bypassing permissionMap() and therefore never seeing
 * `role_settings` — the map the server actually enforces. Where the snapshot is
 * null or empty (most users) every one of those checks silently answers false.
 *
 * Fails CLOSED, and like hasPermission() it does NOT apply the admin bypass —
 * callers call isFullAdmin() first, visibly.
 */
export function holdsKey(user: any, key: string): boolean {
    if (!key) return false;
    return permissionMap(user)[String(key)] === true;
}

/**
 * Does this user hold ANY of these permission keys? (OR semantics.)
 *
 * Mirrors the server's `permission:a,b` middleware, which grants when any one
 * of the listed keys is held.
 */
export function holdsAnyKey(user: any, keys: string[]): boolean {
    return (keys || []).some((k) => holdsKey(user, k));
}
